import { API } from "./api.service";
import { handleResponse } from "../helpers";
import { API_URL } from "../config/appConfig";

const unwrapProfile = (payload = {}) => {
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return unwrapProfile(payload.data);
  }
  if (payload?.user && typeof payload.user === "object") {
    return unwrapProfile(payload.user);
  }
  return payload;
};

const isMissingRoute = (err) => {
  const code = Number(err?.code || err?.status || 0);
  return code === 404 || code === 405 || code === 501;
};

const tryThenFallback = async (primary, fallback) => {
  try {
    return await primary();
  } catch (err) {
    if (!fallback || !isMissingRoute(err)) {
      throw err;
    }
    return fallback();
  }
};

const profileBody = (payload = {}) => {
  const body = {
    firstname: payload.firstname || "",
    lastname: payload.lastname || "",
    email: payload.email || "",
  };
  if (payload.mobile) {
    body.mobile = payload.mobile;
  }
  return body;
};

const getSystemProfile = async () => {
  return API.get(`${API_URL.idp}/system/user/profile`, "system_profile")
    .then(handleResponse)
    .then(unwrapProfile);
};

const updateSystemProfile = async (payload) => {
  return API.put(
    `${API_URL.idp}/system/user/profile`,
    "system_profile_update",
    profileBody(payload)
  ).then(handleResponse);
};

const changeSystemOwnPassword = async (payload) => {
  return API.put(
    `${API_URL.idp}/system/user/changepassword`,
    "system_own_change_password",
    { password: payload?.password || payload?.newPassword || "" }
  ).then(handleResponse);
};

const getTenantProfile = async (userId) => {
  // Prefer self profile API (firstname/lastname). Fall back to get-by-id (often only `name`).
  const bySelf = () =>
    API.get(`${API_URL.idp}/user/profile`, "tenant_profile")
      .then(handleResponse)
      .then(unwrapProfile);
  const byId = userId
    ? () =>
        API.get(`${API_URL.idp}/user/${userId}`, "tenant_profile_by_id")
          .then(handleResponse)
          .then(unwrapProfile)
    : null;

  return byId ? tryThenFallback(bySelf, byId) : bySelf();
};

const updateTenantProfile = async (payload, userId) => {
  const body = profileBody(payload);
  const bySelf = () =>
    API.put(`${API_URL.idp}/user/profile`, "tenant_profile_update", body).then(
      handleResponse
    );
  const byId = userId
    ? () =>
        API.put(`${API_URL.idp}/user/${userId}`, "tenant_profile_update_by_id", {
          ...body,
          ...(payload?.username ? { username: payload.username } : {}),
        }).then(handleResponse)
    : null;

  return tryThenFallback(bySelf, byId);
};

const changeTenantOwnPassword = async (payload = {}, tenantId) => {
  return API.put(
    `${API_URL.idp}/user/password`,
    "tenant_own_change_password",
    {
      password: payload.password || payload.newPassword || "",
      oldPassword: payload.oldPassword || "",
    },
    {
      headers: {
        ...(tenantId ? { tenantId: String(tenantId).toLowerCase() } : {}),
      },
    }
  ).then(handleResponse);
};

export const accountService = {
  getSystemProfile,
  updateSystemProfile,
  changeSystemOwnPassword,
  getTenantProfile,
  updateTenantProfile,
  changeTenantOwnPassword,
};
